// client/render — secret: three.js scene graph, toon shader, outline pass, camera math.
// Public surface: mount, addEntity, removeEntity, frame, setCameraTarget, raycastGround.
// Per ESLint rule: only render/, world/, dungeons/, app/ may import three.
//
// userData conventions on three.js objects:
//   noPick: true            → exclude this mesh from pickEntity's raycast hit set
//                             (use for purely decorative sub-meshes like glow rings).
//   isNodeHighlight: true   → identifies a resource node's tier-colored glow ring.
//                             setReticleTarget hides this while the reticle is on the node
//                             to avoid z-fight with the orange reticle ring.

import {
  Scene,
  WebGLRenderer,
  OrthographicCamera,
  Vector3,
  Vector2,
  Mesh,
  Group,
  HemisphereLight,
  DirectionalLight,
  Object3D,
  Raycaster,
  Plane,
  Color,
  PCFSoftShadowMap,
  CapsuleGeometry,
  SphereGeometry,
  BoxGeometry,
  CylinderGeometry,
  ConeGeometry,
  RingGeometry,
  IcosahedronGeometry,
  MeshBasicMaterial,
  AdditiveBlending,
  DoubleSide,
  Fog,
  ACESFilmicToneMapping,
  SRGBColorSpace,
} from 'three';
import { makeToon, makeOutline } from './toon.js';

export type EntityId = string;

export interface RenderEntity {
  id: EntityId;
  object: Object3D;
}

const scene = new Scene();
const horizonColor = new Color('#c9a06a'); // golden-hour Greenshade
scene.background = horizonColor;
scene.fog = new Fog(horizonColor, 35, 95);

const ambient = new HemisphereLight(0xfdd9a0, 0x3a5028, 1.05);
scene.add(ambient);

const sun = new DirectionalLight(0xffe6b8, 1.6);
sun.position.set(40, 80, 30);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.bias = -0.0005;
sun.shadow.normalBias = 0.02;
sun.shadow.camera.near = 1;
sun.shadow.camera.far = 200;
sun.shadow.camera.left = -60;
sun.shadow.camera.right = 60;
sun.shadow.camera.top = 60;
sun.shadow.camera.bottom = -60;
scene.add(sun);

let renderer: WebGLRenderer | null = null;
let camera: OrthographicCamera | null = null;
const cameraTarget = new Vector3(0, 0, 0);
const cameraOffset = new Vector3(); // computed from pitch/yaw

const PITCH_DEG = 45;
const YAW_DEG = 30;
const VIEW_DISTANCE = 60;
let zoom = 1;

function recomputeCameraOffset(): void {
  const pitch = (PITCH_DEG * Math.PI) / 180;
  const yaw = (YAW_DEG * Math.PI) / 180;
  cameraOffset.set(
    Math.sin(yaw) * Math.cos(pitch),
    Math.sin(pitch),
    Math.cos(yaw) * Math.cos(pitch)
  ).multiplyScalar(VIEW_DISTANCE);
}
recomputeCameraOffset();

function updateCameraProjection(): void {
  if (!camera || !renderer) return;
  const w = renderer.domElement.clientWidth || 1;
  const h = renderer.domElement.clientHeight || 1;
  const viewSize = 30 * zoom;
  const aspect = w / h;
  camera.left = -viewSize * aspect;
  camera.right = viewSize * aspect;
  camera.top = viewSize;
  camera.bottom = -viewSize;
  camera.near = 0.1;
  camera.far = 500;
  camera.updateProjectionMatrix();
}

const entities = new Map<EntityId, RenderEntity>();

export function mount(host: HTMLElement): void {
  if (renderer) throw new Error('client/render.mount: already mounted');
  renderer = new WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = SRGBColorSpace;
  host.appendChild(renderer.domElement);

  camera = new OrthographicCamera();
  updateCameraProjection();

  const resize = (): void => {
    if (!renderer) return;
    const w = host.clientWidth;
    const h = host.clientHeight;
    renderer.setSize(w, h, false);
    updateCameraProjection();
  };
  resize();
  window.addEventListener('resize', resize);
}

export function addEntity(e: RenderEntity): void {
  if (entities.has(e.id)) {
    throw new Error(`client/render.addEntity: id ${e.id} already added`);
  }
  entities.set(e.id, e);
  scene.add(e.object);
}

export function removeEntity(id: EntityId): void {
  const e = entities.get(id);
  if (!e) return;
  scene.remove(e.object);
  entities.delete(id);
}

export function setCameraTarget(pos: { x: number; y: number; z: number }): void {
  cameraTarget.set(pos.x, pos.y, pos.z);
}

export function setZoom(value: number): void {
  zoom = Math.max(0.5, Math.min(2.5, value));
  updateCameraProjection();
}

export function frame(_dtMs: number): void {
  if (!renderer || !camera) return;
  camera.position.copy(cameraTarget).add(cameraOffset);
  camera.lookAt(cameraTarget);
  sun.position.copy(cameraTarget).add(new Vector3(40, 80, 30));
  sun.target.position.copy(cameraTarget);
  sun.target.updateMatrixWorld();
  renderer.render(scene, camera);
}

const raycaster = new Raycaster();
const groundPlane = new Plane(new Vector3(0, 1, 0), 0);
const tmpVec = new Vector3();

export function raycastGround(clientX: number, clientY: number): { x: number; z: number } | null {
  if (!renderer || !camera) return null;
  const rect = renderer.domElement.getBoundingClientRect();
  const ndc = new Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  );
  raycaster.setFromCamera(ndc, camera);
  const hit = raycaster.ray.intersectPlane(groundPlane, tmpVec);
  if (!hit) return null;
  return { x: tmpVec.x, z: tmpVec.z };
}

export function pickEntity(clientX: number, clientY: number, candidateIds: string[]): string | null {
  if (!renderer || !camera) return null;
  const rect = renderer.domElement.getBoundingClientRect();
  const ndc = new Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  );
  raycaster.setFromCamera(ndc, camera);
  let best: { id: string; dist: number } | null = null;
  for (const id of candidateIds) {
    const e = entities.get(id);
    if (!e) continue;
    if (!e.object.visible) continue;
    const hits = raycaster.intersectObject(e.object, true);
    // Skip decorative sub-meshes (glow rings etc.) flagged with userData.noPick.
    const first = hits.find((h) => !h.object.userData['noPick']);
    if (!first) continue;
    if (!best || first.distance < best.dist) {
      best = { id, dist: first.distance };
    }
  }
  return best ? best.id : null;
}

const projectVec = new Vector3();
export function projectToScreen(p: { x: number; y: number; z: number }): { x: number; y: number; behind: boolean } | null {
  if (!renderer || !camera) return null;
  projectVec.set(p.x, p.y, p.z);
  projectVec.project(camera);
  const rect = renderer.domElement.getBoundingClientRect();
  return {
    x: rect.left + ((projectVec.x + 1) / 2) * rect.width,
    y: rect.top + ((1 - projectVec.y) / 2) * rect.height,
    behind: projectVec.z > 1 || projectVec.z < -1,
  };
}

export function setEntityVisible(id: string, visible: boolean): void {
  const e = entities.get(id);
  if (!e) return;
  e.object.visible = visible;
}

export function setEntityTransform(
  id: string,
  pos: { x: number; y: number; z: number },
  yaw: number
): void {
  const e = entities.get(id);
  if (!e) return;
  e.object.position.set(pos.x, pos.y, pos.z);
  e.object.rotation.y = yaw;
}

// ---------- VFX ----------

interface Vfx {
  obj: Object3D;
  startMs: number;
  durationMs: number;
  follow?: { id: string; offsetY: number };
  update: (vfx: Vfx, t: number, nowMs: number) => void; // t in [0,1]
  onEnd?: () => void;
}

const vfxList: Vfx[] = [];

function addVfx(v: Vfx): void {
  scene.add(v.obj);
  vfxList.push(v);
}

export function frameVfx(nowMs: number): void {
  for (let i = vfxList.length - 1; i >= 0; i--) {
    const v = vfxList[i];
    if (!v) continue;
    const t = Math.min(1, (nowMs - v.startMs) / v.durationMs);
    if (v.follow) {
      const e = entities.get(v.follow.id);
      if (e) {
        v.obj.position.set(
          e.object.position.x,
          e.object.position.y + v.follow.offsetY,
          e.object.position.z
        );
      }
    }
    v.update(v, t, nowMs);
    if (t >= 1) {
      scene.remove(v.obj);
      v.onEnd?.();
      vfxList.splice(i, 1);
    }
  }
}

export function spawnHitFlash(pos: { x: number; y: number; z: number }, color = '#ffe080'): void {
  const geo = new SphereGeometry(0.4, 12, 8);
  const mat = new MeshBasicMaterial({ color, transparent: true, opacity: 0.9, blending: AdditiveBlending });
  const mesh = new Mesh(geo, mat);
  mesh.position.set(pos.x, pos.y, pos.z);
  addVfx({
    obj: mesh,
    startMs: performance.now(),
    durationMs: 220,
    update(_v, t) {
      const s = 1 + t * 1.6;
      mesh.scale.set(s, s, s);
      mat.opacity = 0.9 * (1 - t);
    },
  });
}

export function spawnSlashArc(pos: { x: number; y: number; z: number }, yaw: number, range = 4): void {
  const geo = new RingGeometry(range * 0.4, range * 0.55, 24, 1, -Math.PI / 2.5, Math.PI / 1.25);
  const mat = new MeshBasicMaterial({
    color: '#fff0a8',
    transparent: true,
    opacity: 0.85,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
  const mesh = new Mesh(geo, mat);
  mesh.position.set(pos.x, pos.y + 0.6, pos.z);
  mesh.rotation.x = -Math.PI / 2.4;
  mesh.rotation.y = yaw;
  addVfx({
    obj: mesh,
    startMs: performance.now(),
    durationMs: 260,
    update(_v, t) {
      mat.opacity = 0.85 * (1 - t);
      mesh.scale.set(1 + t * 0.4, 1, 1 + t * 0.4);
    },
  });
}

export function spawnArrowStreak(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number }
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dy, dz);
  if (length < 0.05) return;
  const geo = new BoxGeometry(0.06, 0.06, length);
  const mat = new MeshBasicMaterial({
    color: '#fff0c0',
    transparent: true,
    opacity: 0.95,
    blending: AdditiveBlending,
  });
  const mesh = new Mesh(geo, mat);
  mesh.position.set(from.x + dx / 2, from.y + dy / 2, from.z + dz / 2);
  mesh.lookAt(to.x, to.y, to.z);
  addVfx({
    obj: mesh,
    startMs: performance.now(),
    durationMs: 200,
    update(_v, t) {
      mat.opacity = 0.95 * (1 - t);
      const s = 1 + t * 0.4;
      mesh.scale.set(s, s, 1);
    },
  });
}

export function spawnFireBurst(pos: { x: number; y: number; z: number }, radius = 1.0): void {
  // Multi-layer burst: core flash + outer expanding ring.
  const core = new Mesh(
    new SphereGeometry(radius * 0.5, 12, 8),
    new MeshBasicMaterial({ color: '#ff8030', transparent: true, opacity: 0.95, blending: AdditiveBlending })
  );
  core.position.set(pos.x, pos.y, pos.z);
  const coreMat = core.material as MeshBasicMaterial;
  addVfx({
    obj: core,
    startMs: performance.now(),
    durationMs: 380,
    update(_v, t) {
      const s = 1 + t * 1.6;
      core.scale.set(s, s, s);
      coreMat.opacity = 0.95 * (1 - t);
    },
  });

  const ring = new Mesh(
    new RingGeometry(radius * 0.4, radius * 0.55, 24),
    new MeshBasicMaterial({
      color: '#ffc060',
      transparent: true,
      opacity: 0.85,
      blending: AdditiveBlending,
      side: DoubleSide,
    })
  );
  ring.position.set(pos.x, pos.y + 0.05, pos.z);
  ring.rotation.x = -Math.PI / 2;
  const ringMat = ring.material as MeshBasicMaterial;
  addVfx({
    obj: ring,
    startMs: performance.now(),
    durationMs: 460,
    update(_v, t) {
      const s = 1 + t * 2.2;
      ring.scale.set(s, s, 1);
      ringMat.opacity = 0.85 * (1 - t);
    },
  });

  // Embers
  for (let i = 0; i < 5; i++) {
    const ember = new Mesh(
      new SphereGeometry(0.07, 6, 4),
      new MeshBasicMaterial({ color: '#ffe080', transparent: true, opacity: 0.9, blending: AdditiveBlending })
    );
    const angle = (i / 5) * Math.PI * 2 + Math.random() * 0.3;
    const r0 = radius * 0.3;
    ember.position.set(pos.x + Math.cos(angle) * r0, pos.y + 0.15, pos.z + Math.sin(angle) * r0);
    const matE = ember.material as MeshBasicMaterial;
    const speed = 0.6 + Math.random() * 0.4;
    addVfx({
      obj: ember,
      startMs: performance.now(),
      durationMs: 600,
      update(_v, t) {
        ember.position.x = pos.x + Math.cos(angle) * (r0 + t * radius * 1.6);
        ember.position.z = pos.z + Math.sin(angle) * (r0 + t * radius * 1.6);
        ember.position.y = pos.y + 0.15 + speed * t * 1.2 - t * t * 1.2;
        matE.opacity = 0.9 * (1 - t);
      },
    });
  }
}

let reticle: Mesh | null = null;
let reticleTargetId: string | null = null;
let suppressedNodeHighlight: Mesh | null = null;
function ensureReticle(): Mesh {
  if (reticle) return reticle;
  const geo = new RingGeometry(0.85, 1.05, 32);
  const mat = new MeshBasicMaterial({
    color: '#ff8060',
    transparent: true,
    opacity: 0.9,
    blending: AdditiveBlending,
    side: DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  reticle = new Mesh(geo, mat);
  reticle.rotation.x = -Math.PI / 2;
  reticle.renderOrder = 1000;
  reticle.visible = false;
  scene.add(reticle);
  return reticle;
}

export function setReticleTarget(id: string | null): void {
  // Restore any previously suppressed node highlight first.
  if (suppressedNodeHighlight) {
    suppressedNodeHighlight.visible = true;
    suppressedNodeHighlight = null;
  }
  reticleTargetId = id;
  const r = ensureReticle();
  if (!id) {
    r.visible = false;
    return;
  }
  // If the new target has a node highlight, hide it while the reticle is on top.
  const e = entities.get(id);
  if (e) {
    e.object.traverse((child) => {
      if (suppressedNodeHighlight) return;
      if ((child as Mesh).isMesh && child.userData['isNodeHighlight']) {
        child.visible = false;
        suppressedNodeHighlight = child as Mesh;
      }
    });
  }
}

export function tickReticle(nowMs: number): void {
  const r = ensureReticle();
  if (!reticleTargetId) {
    r.visible = false;
    return;
  }
  const e = entities.get(reticleTargetId);
  if (!e || !e.object.visible) {
    r.visible = false;
    return;
  }
  r.visible = true;
  r.position.set(e.object.position.x, e.object.position.y + 0.04, e.object.position.z);
  const pulse = 1 + Math.sin(nowMs / 180) * 0.06;
  r.scale.set(pulse, pulse, pulse);
}

export interface CharacterRig {
  group: Group;
  bob: { rig: Object3D };
}

export function makeCharacter(opts: { tunic: string; pants: string; skin: string }): CharacterRig {
  const group = new Group();
  const bobRig = new Group();
  group.add(bobRig);

  // Body (tunic): tapered capsule
  const bodyGeo = new CapsuleGeometry(0.36, 0.7, 4, 10);
  const body = new Mesh(bodyGeo, makeToon(opts.tunic));
  body.castShadow = true;
  body.position.y = 0.95;
  bobRig.add(body);
  const bodyOutline = new Mesh(bodyGeo, makeOutline(0.05));
  bodyOutline.position.copy(body.position);
  bobRig.add(bodyOutline);

  // Head: sphere on top, smaller proportion (chibi-ish)
  const headGeo = new SphereGeometry(0.32, 14, 12);
  const head = new Mesh(headGeo, makeToon(opts.skin));
  head.castShadow = true;
  head.position.y = 1.7;
  bobRig.add(head);
  const headOutline = new Mesh(headGeo, makeOutline(0.05));
  headOutline.position.copy(head.position);
  bobRig.add(headOutline);

  // Hair cap: slightly larger half-sphere on head
  const hairGeo = new SphereGeometry(0.34, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  const hair = new Mesh(hairGeo, makeToon('#3a2820'));
  hair.castShadow = true;
  hair.position.y = 1.74;
  bobRig.add(hair);
  const hairOutline = new Mesh(hairGeo, makeOutline(0.05));
  hairOutline.position.copy(hair.position);
  bobRig.add(hairOutline);

  // Pants: short cylinder under body
  const pantsGeo = new CylinderGeometry(0.32, 0.28, 0.45, 10);
  const pants = new Mesh(pantsGeo, makeToon(opts.pants));
  pants.castShadow = true;
  pants.position.y = 0.4;
  bobRig.add(pants);
  const pantsOutline = new Mesh(pantsGeo, makeOutline(0.05));
  pantsOutline.position.copy(pants.position);
  bobRig.add(pantsOutline);

  // Cape: thin slightly-curved box hanging from shoulders
  const capeGeo = new BoxGeometry(0.6, 0.85, 0.05);
  const cape = new Mesh(capeGeo, makeToon('#5a2030'));
  cape.castShadow = true;
  cape.position.set(0, 1.05, 0.18);
  cape.rotation.x = -0.15;
  bobRig.add(cape);
  const capeOutline = new Mesh(capeGeo, makeOutline(0.04));
  capeOutline.position.copy(cape.position);
  capeOutline.rotation.copy(cape.rotation);
  bobRig.add(capeOutline);

  // Sword on hip: small grip + blade
  const sword = new Group();
  sword.position.set(-0.32, 0.7, 0);
  sword.rotation.set(0, 0, Math.PI / 2.4);
  const gripGeo = new CylinderGeometry(0.04, 0.04, 0.16, 6);
  const grip = new Mesh(gripGeo, makeToon('#2a1810'));
  grip.castShadow = true;
  sword.add(grip);
  const bladeGeo = new BoxGeometry(0.06, 0.6, 0.015);
  const blade = new Mesh(bladeGeo, makeToon('#c8c8d0'));
  blade.castShadow = true;
  blade.position.y = 0.38;
  sword.add(blade);
  const bladeOutline = new Mesh(bladeGeo, makeOutline(0.025));
  bladeOutline.position.copy(blade.position);
  sword.add(bladeOutline);
  bobRig.add(sword);

  return { group, bob: { rig: bobRig } };
}

export function tickCharacterIdle(rig: CharacterRig, nowMs: number, moving: boolean): void {
  const t = nowMs / 1000;
  if (moving) {
    rig.bob.rig.position.y = Math.abs(Math.sin(t * 8)) * 0.08;
  } else {
    rig.bob.rig.position.y = Math.sin(t * 2) * 0.04;
  }
}

export interface WolfRig {
  group: Group;
  bobRig: Object3D;
  legs: Object3D[];
}

export function makeWolf(): WolfRig {
  const group = new Group();
  const bobRig = new Group();
  group.add(bobRig);

  const furDark = '#3a3530';
  const furMid = '#5a4a3c';
  const furLight = '#8a7a5c';

  // Body: stretched capsule, lying horizontal
  const bodyGeo = new CapsuleGeometry(0.3, 0.7, 4, 10);
  const body = new Mesh(bodyGeo, makeToon(furMid));
  body.castShadow = true;
  body.rotation.z = Math.PI / 2;
  body.position.y = 0.55;
  bobRig.add(body);
  const bodyOutline = new Mesh(bodyGeo, makeOutline(0.04));
  bodyOutline.position.copy(body.position);
  bodyOutline.rotation.copy(body.rotation);
  bobRig.add(bodyOutline);

  // Head: rounded box at front
  const headGeo = new BoxGeometry(0.4, 0.36, 0.42);
  const head = new Mesh(headGeo, makeToon(furMid));
  head.castShadow = true;
  head.position.set(0, 0.6, 0.55);
  bobRig.add(head);
  const headOutline = new Mesh(headGeo, makeOutline(0.04));
  headOutline.position.copy(head.position);
  bobRig.add(headOutline);

  // Snout: smaller cone-ish box
  const snoutGeo = new BoxGeometry(0.22, 0.18, 0.26);
  const snout = new Mesh(snoutGeo, makeToon(furDark));
  snout.castShadow = true;
  snout.position.set(0, 0.55, 0.85);
  bobRig.add(snout);
  const snoutOutline = new Mesh(snoutGeo, makeOutline(0.035));
  snoutOutline.position.copy(snout.position);
  bobRig.add(snoutOutline);

  // Ears: two small triangle cones
  for (const sx of [-1, 1]) {
    const earGeo = new ConeGeometry(0.08, 0.18, 4);
    const ear = new Mesh(earGeo, makeToon(furDark));
    ear.castShadow = true;
    ear.position.set(0.12 * sx, 0.85, 0.42);
    ear.rotation.z = sx * 0.18;
    bobRig.add(ear);
    const earOutline = new Mesh(earGeo, makeOutline(0.03));
    earOutline.position.copy(ear.position);
    earOutline.rotation.copy(ear.rotation);
    bobRig.add(earOutline);
  }

  // Tail: cone pointing back
  const tailGeo = new ConeGeometry(0.08, 0.5, 6);
  const tail = new Mesh(tailGeo, makeToon(furLight));
  tail.castShadow = true;
  tail.position.set(0, 0.65, -0.55);
  tail.rotation.x = -Math.PI / 2.4;
  bobRig.add(tail);

  // 4 legs: short cylinders at corners
  const legs: Object3D[] = [];
  const legGeo = new CylinderGeometry(0.07, 0.06, 0.5, 6);
  for (const [dx, dz] of [
    [-0.18, 0.3],
    [0.18, 0.3],
    [-0.18, -0.3],
    [0.18, -0.3],
  ] as const) {
    const leg = new Mesh(legGeo, makeToon(furDark));
    leg.castShadow = true;
    leg.position.set(dx, 0.25, dz);
    bobRig.add(leg);
    const legOutline = new Mesh(legGeo, makeOutline(0.035));
    legOutline.position.copy(leg.position);
    bobRig.add(legOutline);
    legs.push(leg);
  }

  return { group, bobRig, legs };
}

export interface ResourceNodeRig {
  group: Group;
  highlight: Mesh;
}

export function makeResourceNode(kind: 'wood' | 'ore' | 'fiber', tier: number): ResourceNodeRig {
  const group = new Group();
  const tierColor: Record<number, string> = {
    1: '#a0c060',
    2: '#60a0c0',
    3: '#c060a0',
    4: '#c0a060',
    5: '#c08040',
  };

  if (kind === 'wood') {
    // A taller, healthier-looking tree (different from world props)
    const trunkH = 2.2 + tier * 0.5;
    const trunk = new Mesh(
      new CylinderGeometry(0.28, 0.4, trunkH, 7),
      makeToon('#5a3a22')
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    group.add(trunk);
    const trunkOutline = new Mesh(trunk.geometry, makeOutline(0.04));
    trunkOutline.position.copy(trunk.position);
    group.add(trunkOutline);

    for (let i = 0; i < 3; i++) {
      const r = 1.6 - i * 0.35;
      const sphere = new Mesh(
        new SphereGeometry(r, 12, 8),
        makeToon(i % 2 === 0 ? '#3a6a28' : '#5a8838')
      );
      sphere.position.y = trunkH + i * 0.6 - 0.2;
      sphere.castShadow = true;
      group.add(sphere);
    }
  } else if (kind === 'ore') {
    // Bigger blocky rock, with chunks of metal showing
    const main = new Mesh(
      new IcosahedronGeometry(0.85, 0),
      makeToon('#6a6058')
    );
    main.scale.y = 0.85;
    main.position.y = 0.55;
    main.castShadow = true;
    main.receiveShadow = true;
    group.add(main);
    const mainOutline = new Mesh(main.geometry, makeOutline(0.04));
    mainOutline.position.copy(main.position);
    mainOutline.scale.copy(main.scale);
    group.add(mainOutline);

    // Metal flecks
    const flecks = ['#c0c0d0', '#a8a8b8', '#909098'];
    for (let i = 0; i < 5; i++) {
      const fleck = new Mesh(
        new IcosahedronGeometry(0.16, 0),
        makeToon(flecks[i % flecks.length] ?? '#c0c0d0')
      );
      const angle = (i / 5) * Math.PI * 2;
      fleck.position.set(Math.cos(angle) * 0.65, 0.55 + Math.sin(i) * 0.2, Math.sin(angle) * 0.65);
      fleck.castShadow = true;
      group.add(fleck);
    }
  } else {
    // Fiber bush — colored cone cluster
    const palette: Record<number, string> = {
      1: '#9aa050',
      2: '#7a8848',
      3: '#5a8030',
    };
    const color = palette[tier] ?? '#7a8848';
    for (let i = 0; i < 7; i++) {
      const cone = new Mesh(
        new ConeGeometry(0.18, 0.7 + Math.random() * 0.3, 5),
        makeToon(color)
      );
      const angle = (i / 7) * Math.PI * 2;
      cone.position.set(Math.cos(angle) * 0.2, 0.35, Math.sin(angle) * 0.2);
      cone.rotation.set(0, angle, (Math.random() - 0.5) * 0.3);
      cone.castShadow = true;
      group.add(cone);
    }
    const center = new Mesh(
      new SphereGeometry(0.25, 8, 6),
      makeToon(color)
    );
    center.position.y = 0.4;
    center.castShadow = true;
    group.add(center);
  }

  // Glow ring under the node — colored by tier. depthTest off so it never gets
  // half-clipped by sloped terrain; renderOrder high so it draws after the world.
  const ringMat = new MeshBasicMaterial({
    color: tierColor[tier] ?? '#80c0ff',
    transparent: true,
    opacity: 0.6,
    blending: AdditiveBlending,
    side: DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  const ring = new Mesh(new RingGeometry(0.7, 0.95, 24), ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  ring.renderOrder = 990;
  ring.userData['noPick'] = true;
  ring.userData['isNodeHighlight'] = true;
  group.add(ring);

  return { group, highlight: ring };
}

export function makeStation(kind: 'refiner' | 'weaponsmith' | 'armorer'): Group {
  const g = new Group();

  // Stone base for all
  const baseGeo = new BoxGeometry(2.0, 0.4, 1.4);
  const base = new Mesh(baseGeo, makeToon('#5a5048'));
  base.position.y = 0.2;
  base.castShadow = true;
  base.receiveShadow = true;
  g.add(base);
  const baseOutline = new Mesh(baseGeo, makeOutline(0.04));
  baseOutline.position.copy(base.position);
  g.add(baseOutline);

  if (kind === 'refiner') {
    // Cauldron
    const cauldron = new Mesh(
      new CylinderGeometry(0.55, 0.45, 0.55, 12),
      makeToon('#3a3530')
    );
    cauldron.position.y = 0.65;
    cauldron.castShadow = true;
    g.add(cauldron);
    const cauldronOutline = new Mesh(cauldron.geometry, makeOutline(0.04));
    cauldronOutline.position.copy(cauldron.position);
    g.add(cauldronOutline);

    // Liquid surface (glowing)
    const liquid = new Mesh(
      new CylinderGeometry(0.5, 0.5, 0.04, 12),
      new MeshBasicMaterial({ color: '#ffa040', transparent: true, opacity: 0.85, blending: AdditiveBlending })
    );
    liquid.position.y = 0.92;
    g.add(liquid);

    // Log on the side
    const log = new Mesh(
      new CylinderGeometry(0.18, 0.18, 1.0, 8),
      makeToon('#5a3a22')
    );
    log.position.set(-0.7, 0.55, 0.4);
    log.rotation.z = Math.PI / 2;
    log.castShadow = true;
    g.add(log);
  } else if (kind === 'weaponsmith') {
    // Anvil
    const anvilTop = new Mesh(
      new BoxGeometry(0.9, 0.18, 0.45),
      makeToon('#3a3a40')
    );
    anvilTop.position.y = 0.7;
    anvilTop.castShadow = true;
    g.add(anvilTop);
    const anvilTopOutline = new Mesh(anvilTop.geometry, makeOutline(0.04));
    anvilTopOutline.position.copy(anvilTop.position);
    g.add(anvilTopOutline);

    const anvilNeck = new Mesh(
      new BoxGeometry(0.4, 0.25, 0.32),
      makeToon('#3a3a40')
    );
    anvilNeck.position.y = 0.5;
    anvilNeck.castShadow = true;
    g.add(anvilNeck);

    // Sword stuck in anvil
    const blade = new Mesh(
      new BoxGeometry(0.05, 0.85, 0.012),
      makeToon('#c8c8d0')
    );
    blade.position.set(0.2, 1.2, 0);
    blade.castShadow = true;
    g.add(blade);
    const bladeOutline = new Mesh(blade.geometry, makeOutline(0.025));
    bladeOutline.position.copy(blade.position);
    g.add(bladeOutline);
    const grip = new Mesh(
      new CylinderGeometry(0.05, 0.05, 0.18, 6),
      makeToon('#2a1810')
    );
    grip.position.set(0.2, 1.7, 0);
    g.add(grip);
  } else if (kind === 'armorer') {
    // Display stand with helm
    const stand = new Mesh(
      new CylinderGeometry(0.06, 0.06, 0.7, 6),
      makeToon('#3a3530')
    );
    stand.position.y = 0.75;
    g.add(stand);
    const helm = new Mesh(
      new SphereGeometry(0.28, 14, 10, 0, Math.PI * 2, 0, Math.PI / 1.7),
      makeToon('#8a8a90')
    );
    helm.position.y = 1.05;
    helm.castShadow = true;
    g.add(helm);
    const helmOutline = new Mesh(helm.geometry, makeOutline(0.04));
    helmOutline.position.copy(helm.position);
    g.add(helmOutline);
    // Plate strap
    const strap = new Mesh(
      new BoxGeometry(0.5, 0.05, 0.5),
      makeToon('#5a4030')
    );
    strap.position.y = 0.95;
    g.add(strap);
  }

  // Glow ring marker — depthTest off so the back half doesn't clip into sloped terrain.
  const ringColor =
    kind === 'refiner' ? '#ff9040' : kind === 'weaponsmith' ? '#a0c8ff' : '#c0c0d0';
  const ring = new Mesh(
    new RingGeometry(1.1, 1.4, 28),
    new MeshBasicMaterial({
      color: ringColor,
      transparent: true,
      opacity: 0.5,
      blending: AdditiveBlending,
      side: DoubleSide,
      depthTest: false,
      depthWrite: false,
    })
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  ring.renderOrder = 990;
  ring.userData['noPick'] = true;
  g.add(ring);

  return g;
}

export function tickResourceNode(rig: ResourceNodeRig, nowMs: number, depleted: boolean): void {
  rig.group.visible = true;
  if (depleted) {
    (rig.highlight.material as MeshBasicMaterial).opacity = 0;
    rig.group.visible = false;
    return;
  }
  const t = nowMs / 280;
  (rig.highlight.material as MeshBasicMaterial).opacity = 0.45 + Math.sin(t) * 0.2;
  rig.highlight.rotation.z = nowMs / 1500;
}

export function tickWolf(rig: WolfRig, nowMs: number, moving: boolean): void {
  const t = nowMs / 1000;
  if (moving) {
    rig.bobRig.position.y = Math.abs(Math.sin(t * 10)) * 0.06;
    // Trot the legs
    for (let i = 0; i < rig.legs.length; i++) {
      const phase = i % 2 === 0 ? 0 : Math.PI;
      const leg = rig.legs[i];
      if (leg) leg.position.z = ((i < 2 ? 1 : -1) * 0.3) + Math.sin(t * 10 + phase) * 0.08;
    }
  } else {
    rig.bobRig.position.y = Math.sin(t * 2.5) * 0.025;
    for (let i = 0; i < rig.legs.length; i++) {
      const leg = rig.legs[i];
      if (leg) leg.position.z = (i < 2 ? 1 : -1) * 0.3;
    }
  }
}
